import type { SessionHostAttributes } from '@/server/models'

import { SQL, type SQLFragment } from '@/client/query'

import {
  type AssociationDefinitions,
  type GroupBy,
  type IncludeAssociations,
  includeAssociationsToSql,
  makeQueryModelV2,
  type QueryOrder,
  queryGroupByToSQL,
  queryLimitToSQL,
  queryOrderToSQL,
} from './common'
import type { HostRole } from './constants'
import SessionQ from './SessionQ'
import TopicQ from './TopicQ'
import UserQ from './UserQ'
import VolunteerActivityQ from './VolunteerActivityQ'

const SessionHostQ = {
  ...makeQueryModelV2<SessionHostAttributes>('session_host')([
    'id',
    'user_id',
    'session_id',
    'deleted_message',
    'deleted_source',
    'request_message',
    'role',
    'attempted_hosting',
    'tutoring_seconds',
    'session_host_volunteer_activity_id',
    'session_prep_volunteer_activity_id',
    'created_at',
    'deleted_at',
    'approved_at',
    'updated_at',
  ]),

  associations: (): AssociationDefinitions => ({
    User: {
      query: UserQ,
      foreignKey: UserQ.id,
      sourceKey: SessionHostQ.user_id,
      // Include soft-deleted users by default, since we keep SessionHost
      // rows even after associated user is deleted.
      paranoid: false,
    },
    Session: {
      query: SessionQ,
      foreignKey: SessionQ.id,
      sourceKey: SessionHostQ.session_id,
    },
  }),

  hasTutoredSessionWithTopicAndAtLeastOneLearner: ({
    userId,
    topicName,
  }: {
    userId: number | SQLFragment<number>
    topicName: string
  }): SQLFragment<boolean> => SQL`
   EXISTS(SELECT 1 ${SessionHostQ.where(
     {
       userId,
       attemptedHosting: true,
       include: [
         {
           association: 'Session',
           required: true,
           include: [
             {
               association: 'Topic',
               required: true,
               where: SQL`${TopicQ.name} = ${topicName}`,
             },
           ],
         },
       ],
     },
     SessionQ.hasAtLeastOneLearner()
   )})`,

  where: (
    {
      id,
      paranoid = true,
      sessionId,
      userId,
      attemptedHosting,
      approved = null,
      role = null,
      include = [],
      groupBy = null,
      order = null,
      limit = null,
    }: {
      id?: number | null | SQLFragment<number>
      paranoid?: boolean
      sessionId?: number | null | SQLFragment<number>
      userId?: number | null | SQLFragment<number>
      attemptedHosting?: boolean
      approved?: boolean | null
      role?: HostRole | null
      include?: IncludeAssociations
      groupBy?: GroupBy | null
      order?: QueryOrder | null
      limit?: number | null
    },
    customWhere: SQLFragment<unknown> | null = null
  ): SQLFragment<SessionHostAttributes> => SQL`
            FROM ${SessionHostQ}
            ${includeAssociationsToSql(SessionHostQ, include)}
            WHERE
                ${id != null ? SQL`${SessionHostQ.id} = ${id} AND` : SQL``}
                ${sessionId != null ? SQL`${SessionHostQ.session_id} = ${sessionId} AND` : SQL``}
                ${userId != null ? SQL`${SessionHostQ.user_id} = ${userId} AND` : SQL``}
                ${paranoid ? SQL`${SessionHostQ.deleted_at} IS NULL AND` : SQL``}
                ${
                  attemptedHosting != null
                    ? SQL`${SessionHostQ.attempted_hosting} IS ${
                        attemptedHosting ? SQL`TRUE` : SQL`FALSE`
                      } AND`
                    : SQL``
                }
                ${
                  approved != null
                    ? SQL`${SessionHostQ.approved_at} IS ${approved ? SQL`NOT` : SQL``} NULL AND`
                    : SQL``
                }
                ${role != null ? SQL`${SessionHostQ.role} = ${role} AND` : SQL``}
                ${customWhere != null ? customWhere : SQL`TRUE`}
            ${queryGroupByToSQL(groupBy)}
            ${queryOrderToSQL(order)}
            ${queryLimitToSQL(limit)}
        `,

  isApproved(): SQLFragment<boolean> {
    return SQL`${SessionHostQ.approved_at} IS NOT NULL`
  },

  getEarnedVolunteerSeconds: (): SQLFragment<number> => SQL`(
    SELECT SUM(${VolunteerActivityQ.duration_seconds}) ${VolunteerActivityQ.where(
      { isApproved: true },
      SQL`${VolunteerActivityQ.id} IN (${SessionHostQ.session_host_volunteer_activity_id}, ${SessionHostQ.session_prep_volunteer_activity_id})`
    )}

  )`,
}

export default SessionHostQ
